import { Box, SystemProps } from '@chakra-ui/react'
import _ from 'lodash'
import { ItList, ItListItem } from 'types/graphql'

import Confetti from 'src/components/widgets/Confetti/Confetti'

import JumpToSection from './JumpToSection/JumpToSection'
import Description from './Sections/Description/Description'
import Disclaimer from './Sections/Disclaimer/Disclaimer'
import DontLoseAccessCard from './Sections/DontLoseAccessCard/DontLoseAccessCard'
import Inside from './Sections/Inside/Inside'
import Intro from './Sections/Intro/Intro'
import ItListDetailsHeader from './Sections/ItListDetailsHeader/ItListDetailsHeader'
import Notes from './Sections/Notes/Notes'
import Reviews from './Sections/Reviews/Reviews'
import SocialProofViewer from './Sections/SocialProofViewer/SocialProofViewer'
import StatusLabel from './Sections/StatusLabel/StatusLabel'
import UnlockAction from './Sections/UnlockAction/UnlockAction'
import useItListDetails from './useItListDetails'

import { DetailsElementProps } from '.'

type DetailsElement = React.FC<DetailsElementProps>

type Details = {
  item?: ItListItem
  Element: DetailsElement
}

type ItemDetailsElement = React.FC<
  DetailsElementProps & { index: number; item: ItListItem }
>

type ItemDetails = {
  item?: ItListItem
  Element: ItemDetailsElement
}

const ItListDetails = ({
  locked,
  itList,
  items,
  isPreview = false,
  ...props
}: {
  locked: boolean
  itList: ItList
  items: ItListItem[]
  isPreview?: boolean
} & SystemProps) => {
  const {
    showConfettis,
    brandColor,
    containerRef,
    detailsElementProps,
    itemsAsItemDetails,
    jumpToItemById,
  } = useItListDetails(locked, itList, items, isPreview)

  const details: (ItemDetails | Details)[] = [
    { Element: ItListDetailsHeader },
    { Element: StatusLabel },
    { Element: Intro },
    { Element: UnlockAction },
    { Element: Description },
    { Element: Inside },
    { Element: Notes },
    { Element: Reviews },
    { Element: DontLoseAccessCard },
    ...itemsAsItemDetails,
    { Element: SocialProofViewer },
    { Element: Disclaimer },
  ]
  let indexOfFirstItem: number | null = null

  return (
    <>
      {showConfettis && <Confetti brandColor={brandColor} />}
      {itList.relationship !== 'owner' && !locked && (
        <JumpToSection
          jump={jumpToItemById}
          items={items}
          brandColor={brandColor}
        />
      )}
      <Box
        ref={containerRef}
        position="relative"
        w={{ base: 'full', lg: isPreview ? 'full' : '744px' }}
        mx="auto"
        pt={{ lg: isPreview ? 0 : 6 }}
        pb={16}
        background="white"
        {...props}
      >
        {details.map((detail, index) => {
          if (detail.item) {
            const Element = detail.Element as ItemDetailsElement
            if (indexOfFirstItem === null) indexOfFirstItem = index
            return (
              <Element
                key={index}
                index={index - indexOfFirstItem}
                item={detail.item}
                {...detailsElementProps}
              />
            )
          } else {
            const Element = detail.Element as DetailsElement
            return <Element key={index} {...detailsElementProps} />
          }
        })}
      </Box>
    </>
  )
}

export default ItListDetails
